"use client";
import type { SelectedStore } from "@components/try-and-buy/types/store";
import type { Product, ProductMedia, ProductVariant } from "@lib/product/types";
import type { PropsWithChildren } from "react";
import { createContext, use, useEffect, useState } from "react";

type TryAndBuyContextModel = {
    productName: string;
    variants: Product["variants"];
    attributes: Product["attributes"];
    media: ProductMedia[];
    sku: string | null;
    selectedStore: SelectedStore | null;
    setSelectedStore: (store: SelectedStore) => void;
    setSku: (sku: string) => void;
    isSuccess: boolean;
    setIsSuccess: (isSuccess: boolean) => void;
    hasStoresWithAvailability: boolean;
    setHasStoresWithAvailability: (hasStores: boolean) => void;
};

type Props = {
    product: Product;
    selectedVariant?: ProductVariant;
    isOpen: boolean;
} & PropsWithChildren;

const TryAndBuyContext = createContext<TryAndBuyContextModel | undefined>(undefined);

export const ContextProvider = ({ children, product, selectedVariant, isOpen }: Props) => {
    const [sku, setSku] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [hasStoresWithAvailability, setHasStoresWithAvailability] = useState(false);

    const { variants, attributes } = product;

    useEffect(() => {
        if (!selectedVariant) return;
        setSku(selectedVariant.sku);
    }, [selectedVariant]);

    useEffect(() => {
        if (isOpen) return;
        setSelectedStore(null);
        setSku(selectedVariant?.sku ?? null);
        setIsSuccess(false);
    }, [isOpen, selectedVariant]);

    return (
        <TryAndBuyContext
            value={{
                productName: product.name,
                media: product.media,
                isSuccess,
                setIsSuccess,
                attributes,
                variants,
                sku,
                selectedStore,
                setSelectedStore,
                setSku,
                hasStoresWithAvailability,
                setHasStoresWithAvailability,
            }}
        >
            {children}
        </TryAndBuyContext>
    );
};

export const useTryAndBuyContext = () => use(TryAndBuyContext);
